import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { GalleryDto, GalleryService } from '@millionshow/api-client';
import { Icon } from '@millionshow/client-libs';
import {
  extractYoutubeId,
  getYoutubeThumbnail,
  GalleryType,
} from '@millionshow/common';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import PaginationComponent from '../../../components/pagination/pagination.component';
import { NotFoundComponent } from '../../../components/not-found/not-found.component';
import { SeoService } from '../../../services/seo.service';

@Component({
  selector: 'app-gallery',
  imports: [
    CommonModule,
    Icon,
    RouterLink,
    PaginationComponent,
    NotFoundComponent,
  ],
  templateUrl: './gallery.page.html',
})
export default class GalleryPage {
  private readonly galleryService = inject<GalleryService>(GalleryService);
  private readonly route = inject<ActivatedRoute>(ActivatedRoute);
  private readonly seoService = inject(SeoService);

  readonly type = this.route.snapshot.data['type'] as GalleryType;

  query = signal<string>('');
  pageNo = signal<number>(1);
  pageSize = signal<number>(8);
  galleryFilter = signal<GalleryType>(this.type);

  private searchSubject = new Subject<string>();

  constructor() {
    const isVideo = this.type === 'VIDEO';

    this.seoService.set({
      title: isVideo ? '영상 갤러리' : '사진 갤러리',
      description: isVideo
        ? '밀리언쇼 영상 하이라이트와 홍보 영상을 확인해보세요.'
        : '밀리언쇼 박람회와 행사 현장 사진을 둘러보세요.',
      path: isVideo ? '/gallery/video-gallery' : '/gallery/photo-gallery',
    });

    this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(searchQuery => {
        this.query.set(searchQuery);
        this.pageNo.set(1);
      });
  }

  $galleryData = rxResource({
    stream: ({ params }) => {
      const { galleryFilter, ...rest } = params;

      return this.galleryService.galleryControllerSearchV1({
        ...rest,
        isExposed: true,
        type: galleryFilter as GalleryDto['type'],
        category: 'GENERAL',
        sourceType: 'MILLIONSHOW' as const,
      });
    },
    params: () => ({
      pageNo: this.pageNo(),
      pageSize: this.pageSize(),
      query: this.query(),
      galleryFilter: this.galleryFilter(),
    }),
  });

  galleryList = computed(() => this.$galleryData.value()?.items || []);

  totalCount = computed(() => {
    return this.$galleryData.value()?.pageInfo?.totalItems || 0;
  });

  currentPage = computed(() => this.pageNo());

  totalPages = computed<number[]>(() => {
    const totalItems = this.$galleryData.value()?.pageInfo?.totalItems || 0;
    const count = Math.max(1, Math.ceil(totalItems / this.pageSize()));
    return Array.from({ length: count }, (_, i) => i + 1);
  });

  extractYoutubeId = extractYoutubeId;

  onSearch(event: Event) {
    const input = event.target as HTMLInputElement;
    this.searchSubject.next(input.value);
  }

  onPageChange(page: number): void {
    this.pageNo.set(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  getThumbnail(item: GalleryDto): string {
    if (item.type === 'VIDEO' && item.videoLink) {
      return getYoutubeThumbnail(item.videoLink);
    }
    return item.photos?.[0] || '';
  }

  getTypeLabel(type: GalleryDto['type']): string {
    return type === 'VIDEO' ? '동영상' : '사진';
  }
}
